/**
 * Country phone data with flags, codes, and mobile number lengths
 * Based on ITU-T E.164 standard
 */

export interface CountryPhoneData {
  code: string; // ISO 3166-1 alpha-2 country code
  name: string;
  dialCode: string; // Country calling code (1-3 digits)
  flag: string; // Emoji flag
  mobileLength: number; // Typical mobile number length (without country code)
  mobileLengthRange?: { min: number; max: number }; // Range if variable
}

export const countryPhoneData: CountryPhoneData[] = [
  // UAE (default)
  { code: "AE", name: "United Arab Emirates", dialCode: "971", flag: "🇦🇪", mobileLength: 9 },
  
  // Common Middle East
  { code: "SA", name: "Saudi Arabia", dialCode: "966", flag: "🇸🇦", mobileLength: 9 },
  { code: "KW", name: "Kuwait", dialCode: "965", flag: "🇰🇼", mobileLength: 8 },
  { code: "QA", name: "Qatar", dialCode: "974", flag: "🇶🇦", mobileLength: 8 },
  { code: "BH", name: "Bahrain", dialCode: "973", flag: "🇧🇭", mobileLength: 8 },
  { code: "OM", name: "Oman", dialCode: "968", flag: "🇴🇲", mobileLength: 8 },
  
  // South Asia
  { code: "PK", name: "Pakistan", dialCode: "92", flag: "🇵🇰", mobileLength: 10 },
  { code: "IN", name: "India", dialCode: "91", flag: "🇮🇳", mobileLength: 10 },
  { code: "BD", name: "Bangladesh", dialCode: "880", flag: "🇧🇩", mobileLength: 10 },
  { code: "LK", name: "Sri Lanka", dialCode: "94", flag: "🇱🇰", mobileLength: 9 },
  
  // Europe
  { code: "GB", name: "United Kingdom", dialCode: "44", flag: "🇬🇧", mobileLength: 10 },
  { code: "FR", name: "France", dialCode: "33", flag: "🇫🇷", mobileLength: 9 },
  { code: "DE", name: "Germany", dialCode: "49", flag: "🇩🇪", mobileLength: 11 },
  { code: "IT", name: "Italy", dialCode: "39", flag: "🇮🇹", mobileLength: 10 },
  { code: "ES", name: "Spain", dialCode: "34", flag: "🇪🇸", mobileLength: 9 },
  { code: "NL", name: "Netherlands", dialCode: "31", flag: "🇳🇱", mobileLength: 9 },
  { code: "BE", name: "Belgium", dialCode: "32", flag: "🇧🇪", mobileLength: 9 },
  { code: "CH", name: "Switzerland", dialCode: "41", flag: "🇨🇭", mobileLength: 9 },
  { code: "AT", name: "Austria", dialCode: "43", flag: "🇦🇹", mobileLength: 10 },
  { code: "SE", name: "Sweden", dialCode: "46", flag: "🇸🇪", mobileLength: 9 },
  { code: "NO", name: "Norway", dialCode: "47", flag: "🇳🇴", mobileLength: 8 },
  { code: "DK", name: "Denmark", dialCode: "45", flag: "🇩🇰", mobileLength: 8 },
  { code: "FI", name: "Finland", dialCode: "358", flag: "🇫🇮", mobileLength: 9 },
  { code: "PL", name: "Poland", dialCode: "48", flag: "🇵🇱", mobileLength: 9 },
  { code: "GR", name: "Greece", dialCode: "30", flag: "🇬🇷", mobileLength: 10 },
  { code: "PT", name: "Portugal", dialCode: "351", flag: "🇵🇹", mobileLength: 9 },
  { code: "IE", name: "Ireland", dialCode: "353", flag: "🇮🇪", mobileLength: 9 },
  
  // North America
  { code: "US", name: "United States", dialCode: "1", flag: "🇺🇸", mobileLength: 10 },
  { code: "CA", name: "Canada", dialCode: "1", flag: "🇨🇦", mobileLength: 10 },
  { code: "MX", name: "Mexico", dialCode: "52", flag: "🇲🇽", mobileLength: 10 },
  
  // Asia Pacific
  { code: "CN", name: "China", dialCode: "86", flag: "🇨🇳", mobileLength: 11 },
  { code: "JP", name: "Japan", dialCode: "81", flag: "🇯🇵", mobileLength: 11 },
  { code: "KR", name: "South Korea", dialCode: "82", flag: "🇰🇷", mobileLength: 10 },
  { code: "SG", name: "Singapore", dialCode: "65", flag: "🇸🇬", mobileLength: 8 },
  { code: "MY", name: "Malaysia", dialCode: "60", flag: "🇲🇾", mobileLength: 9 },
  { code: "TH", name: "Thailand", dialCode: "66", flag: "🇹🇭", mobileLength: 9 },
  { code: "PH", name: "Philippines", dialCode: "63", flag: "🇵🇭", mobileLength: 10 },
  { code: "ID", name: "Indonesia", dialCode: "62", flag: "🇮🇩", mobileLength: 10 },
  { code: "VN", name: "Vietnam", dialCode: "84", flag: "🇻🇳", mobileLength: 9 },
  { code: "AU", name: "Australia", dialCode: "61", flag: "🇦🇺", mobileLength: 9 },
  { code: "NZ", name: "New Zealand", dialCode: "64", flag: "🇳🇿", mobileLength: 9 },
  
  // Africa
  { code: "ZA", name: "South Africa", dialCode: "27", flag: "🇿🇦", mobileLength: 9 },
  { code: "EG", name: "Egypt", dialCode: "20", flag: "🇪🇬", mobileLength: 10 },
  { code: "NG", name: "Nigeria", dialCode: "234", flag: "🇳🇬", mobileLength: 10 },
  { code: "KE", name: "Kenya", dialCode: "254", flag: "🇰🇪", mobileLength: 9 },
  
  // South America
  { code: "BR", name: "Brazil", dialCode: "55", flag: "🇧🇷", mobileLength: 11 },
  { code: "AR", name: "Argentina", dialCode: "54", flag: "🇦🇷", mobileLength: 10 },
  { code: "CL", name: "Chile", dialCode: "56", flag: "🇨🇱", mobileLength: 9 },
  { code: "CO", name: "Colombia", dialCode: "57", flag: "🇨🇴", mobileLength: 10 },
  
  // Other
  { code: "TR", name: "Turkey", dialCode: "90", flag: "🇹🇷", mobileLength: 10 },
  { code: "RU", name: "Russia", dialCode: "7", flag: "🇷🇺", mobileLength: 10 },
  { code: "IL", name: "Israel", dialCode: "972", flag: "🇮🇱", mobileLength: 9 },
  { code: "JO", name: "Jordan", dialCode: "962", flag: "🇯🇴", mobileLength: 9 },
  { code: "LB", name: "Lebanon", dialCode: "961", flag: "🇱🇧", mobileLength: 8 },
];

/**
 * Get country data by dial code
 */
export const getCountryByDialCode = (dialCode: string): CountryPhoneData | undefined => {
  return countryPhoneData.find((country) => country.dialCode === dialCode);
};

/**
 * Get country data by country code
 */
export const getCountryByCode = (code: string): CountryPhoneData | undefined => {
  return countryPhoneData.find((country) => country.code.toLowerCase() === code.toLowerCase());
};

/**
 * Get validation rules for a country
 */
export const getPhoneValidationRules = (country: CountryPhoneData | undefined, label: string, isRequired: boolean) => {
  if (!country) {
    return {
      required: isRequired ? `${label} is required` : false,
      minLength: { value: 8, message: `${label} must be at least 8 digits` },
      maxLength: { value: 15, message: `${label} must be at most 15 digits` },
    };
  }

  const { mobileLength, mobileLengthRange } = country;
  
  if (mobileLengthRange) {
    return {
      required: isRequired ? `${label} is required` : false,
      minLength: { 
        value: mobileLengthRange.min, 
        message: `${label} must be at least ${mobileLengthRange.min} digits for ${country.name}` 
      },
      maxLength: { 
        value: mobileLengthRange.max, 
        message: `${label} must be at most ${mobileLengthRange.max} digits for ${country.name}` 
      },
    };
  }

  return {
    required: isRequired ? `${label} is required` : false,
    minLength: { 
      value: mobileLength, 
      message: `${label} must be exactly ${mobileLength} digits for ${country.name}` 
    },
    maxLength: { 
      value: mobileLength, 
      message: `${label} must be exactly ${mobileLength} digits for ${country.name}` 
    },
  };
};
